import { Link } from "react-router-dom";
import { useMetadataCoverage } from "@/hooks/queries/useMetadataCoverage";
import { useExtractionRate } from "@/hooks/queries/useExtractionRate";
import { useTopDescriptors } from "@/hooks/queries/useTopDescriptors";
import { useFailedExtractionCount } from "@/hooks/queries/useFailedExtractionCount";

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function SectionHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function LoadingRow() {
  return (
    <tr>
      <td colSpan={3} className="py-4 text-sm text-gray-400 text-center">
        Loading…
      </td>
    </tr>
  );
}

function EmptyRow({ message }: { message: string }) {
  return (
    <tr>
      <td colSpan={3} className="py-4 text-sm text-gray-400 text-center">
        {message}
      </td>
    </tr>
  );
}

export default function MemoryMetadata() {
  const coverage = useMetadataCoverage();
  const rate = useExtractionRate();
  const memDescriptors = useTopDescriptors("memory", 30);
  const photoDescriptors = useTopDescriptors("photo", 30);
  const failed = useFailedExtractionCount();

  const failedCount = failed.data ?? 0;

  return (
    <div className="p-8 max-w-5xl space-y-10">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Memory Metadata</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Extraction pipeline health and emerging descriptor vocabulary.
        </p>
      </div>

      {/* ── 1. Coverage ────────────────────────────────────────────────────────── */}
      <section>
        <SectionHeader
          title="Coverage"
          sub="Memories with AI metadata extracted vs. total archive."
        />
        {coverage.isLoading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : coverage.isError ? (
          <p className="text-sm text-red-500">
            Failed to load: {coverage.error?.message}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <StatCard
              label="Total memories"
              value={coverage.data?.total ?? 0}
            />
            <StatCard
              label="Extracted"
              value={`${coverage.data?.extracted ?? 0} / ${coverage.data?.total ?? 0}`}
              sub={coverage.data?.extractedPercent}
            />
            <StatCard
              label="With embedding"
              value={`${coverage.data?.withEmbedding ?? 0} / ${coverage.data?.total ?? 0}`}
              sub={coverage.data?.embeddingPercent}
            />
          </div>
        )}
      </section>

      {/* ── 2. Failed extractions ──────────────────────────────────────────────── */}
      {!failed.isLoading && failedCount > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-red-800">
              {failedCount} {failedCount === 1 ? "memory" : "memories"} older than 5 minutes with no extraction
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              extraction_version = 0 and created_at &lt; now() − 5 min
            </p>
          </div>
        </div>
      )}
      {!failed.isLoading && failedCount === 0 && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3">
          <p className="text-sm text-green-800">
            No stuck extractions — pipeline looks healthy.
          </p>
        </div>
      )}

      {/* ── 3. Extraction rate (last 7 days) ──────────────────────────────────── */}
      <section>
        <SectionHeader
          title="Extraction rate — last 7 days"
          sub="Memories whose metadata_extracted_at fell within each day."
        />
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-right px-4 py-2.5 w-24">Extracted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rate.isLoading ? (
                <tr>
                  <td colSpan={2} className="px-4 py-4 text-gray-400 text-center text-sm">
                    Loading…
                  </td>
                </tr>
              ) : rate.isError ? (
                <tr>
                  <td colSpan={2} className="px-4 py-4 text-red-500 text-sm">
                    Failed to load: {rate.error?.message}
                  </td>
                </tr>
              ) : (
                (rate.data ?? []).map((row) => (
                  <tr key={row.date} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-700 font-mono text-xs">
                      {row.date}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {row.count === 0 ? (
                        <span className="text-gray-300">0</span>
                      ) : (
                        <span className="font-medium text-gray-900">{row.count}</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── 4. Top memory descriptors ─────────────────────────────────────────── */}
      <section>
        <SectionHeader
          title="Top memory descriptors — last 30 days"
          sub="Free-form descriptors from memories.descriptors[], ranked by frequency."
        />
        <DescriptorTable
          isLoading={memDescriptors.isLoading}
          isError={memDescriptors.isError}
          error={memDescriptors.error}
          data={memDescriptors.data}
          linkPrefix="/memories/"
        />
      </section>

      {/* ── 5. Top photo scene descriptors ───────────────────────────────────── */}
      <section>
        <SectionHeader
          title="Top photo scene descriptors — last 30 days"
          sub="Free-form descriptors from memory_artefacts.scene_descriptors[], ranked by frequency."
        />
        <DescriptorTable
          isLoading={photoDescriptors.isLoading}
          isError={photoDescriptors.isError}
          error={photoDescriptors.error}
          data={photoDescriptors.data}
          linkPrefix="/memories/"
        />
      </section>
    </div>
  );
}

function DescriptorTable({
  isLoading,
  isError,
  error,
  data,
  linkPrefix,
}: {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  data: Array<{ descriptor: string; count: number; example_id: string }> | undefined;
  linkPrefix: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <th className="text-left px-4 py-2.5">#</th>
            <th className="text-left px-4 py-2.5">Descriptor</th>
            <th className="text-right px-4 py-2.5 w-20">Count</th>
            <th className="text-left px-4 py-2.5 w-32">Example</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {isLoading ? (
            <LoadingRow />
          ) : isError ? (
            <tr>
              <td colSpan={4} className="px-4 py-4 text-red-500 text-sm">
                Failed to load: {error?.message}
              </td>
            </tr>
          ) : !data || data.length === 0 ? (
            <EmptyRow message="No descriptors yet — pipeline hasn't run on any memories in this window." />
          ) : (
            data.map((row, i) => (
              <tr key={row.descriptor} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-400 tabular-nums w-8">
                  {i + 1}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-gray-800">
                  {row.descriptor}
                </td>
                <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-900">
                  {row.count}
                </td>
                <td className="px-4 py-2">
                  <Link
                    to={`${linkPrefix}${row.example_id}`}
                    className="text-xs text-orange-600 hover:underline font-mono"
                  >
                    {row.example_id.slice(0, 8)}…
                  </Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
